import { m } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration';
// @mui
import { alpha } from '@mui/material/styles';
import { IconButton, Stack, Typography } from '@mui/material';
// utils
import { fData } from '../../../utils/formatNumber';
//
import Iconify from '../../iconify';
import { varFade } from '../../animate';
import FileThumbnail, { fileData } from '../../file-thumbnail';
//
import { UploadProps } from '../types';
import { useEffect, useMemo, useState } from 'react';

// ----------------------------------------------------------------------

export default function MultiFilePreview({ thumbnail, files, onRemove, sx, onDragMove }: UploadProps) {
  const defaultFiles = useMemo(() => {
    if (!files) {
      return []
    }

    return files.map((file, index) => ({
      id: `file-${typeof file === 'string' ? file : file.preview || index}`,
      file,
    }));
  }, [files])
  const [draggableFiles, setDraggableFiles] = useState(defaultFiles);

  useEffect(() => {
    setDraggableFiles(defaultFiles);
  }, [defaultFiles]);

  if (!draggableFiles.length) {
    return null;
  }

  function onDragEnd(result) {
    if (!result.destination) {
      return;
    }

    const reorderedFiles = Array.from(draggableFiles);
    const [removed] = reorderedFiles.splice(result.source.index, 1);
    reorderedFiles.splice(result.destination.index, 0, removed);

    setDraggableFiles(reorderedFiles);

    if (onDragMove) {
      onDragMove(reorderedFiles);
    }
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable" direction="horizontal">
        {(provided, snapshot) => (
            <Stack
              direction="row"
              alignItems="center"
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
              }}
            >
              {draggableFiles.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{ ...provided.draggableProps.style }}
                    >
                      <Preview file={item.file} thumbnail={thumbnail} onRemove={onRemove} sx={sx} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function Preview({ file, thumbnail, onRemove, sx }: UploadProps) {
  if (!file) {
    return null;
  }

  const { key, name = '', size = 0 } = fileData(file);

  const isNotFormatFile = typeof file === 'string';

  if (thumbnail) {
    return (
      <Stack
        key={key}
        component={m.div}
        {...varFade().inUp}
        alignItems="center"
        display="inline-flex"
        justifyContent="center"
        sx={{
          m: 0.5,
          width: 80,
          height: 80,
          borderRadius: 1.25,
          overflow: 'hidden',
          position: 'relative',
          border: (theme) => `solid 1px ${theme.palette.divider}`,
          ...sx,
        }}
      >
        <FileThumbnail
          tooltip
          imageView
          file={file}
          sx={{ position: 'absolute' }}
          imgSx={{ position: 'absolute' }}
        />

        {onRemove && (
          <IconButton
            size="small"
            onClick={() => onRemove(file)}
            sx={{
              top: 4,
              right: 4,
              p: '1px',
              position: 'absolute',
              color: (theme) => alpha(theme.palette.common.white, 0.72),
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
              },
            }}
          >
            <Iconify icon="eva:close-fill" width={16} />
          </IconButton>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      key={key}
      component={m.div}
      {...varFade().inUp}
      spacing={2}
      direction="row"
      alignItems="center"
      sx={{
        my: 1,
        px: 1,
        py: 0.75,
        borderRadius: 0.75,
        border: (theme) => `solid 1px ${theme.palette.divider}`,
        ...sx,
      }}
    >
      <FileThumbnail file={file} />

      <Stack flexGrow={1} sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {isNotFormatFile ? file : name}
        </Typography>

        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {isNotFormatFile ? '' : fData(size)}
        </Typography>
      </Stack>

      {onRemove && (
        <IconButton edge="end" size="small" onClick={() => onRemove(file)}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      )}
    </Stack>
  );
}
